// ── The 9 honest postures + neutral copy + retry policy (release contract) ──
// Wave 0 foundation. This is the single posture vocabulary every coaching screen
// renders through <HonestState> (the router) and reads from useHonestQuery.
//
// Release rule (asserted in tests, gates the commit): a gate / denied /
// not-configured / unavailable response must NEVER render as a plain empty
// list, and loading is a skeleton — never a blank. Copy here is neutral and
// never leaks secrets, cross-org existence, or provider names.
//
// Built OVER the existing LiveDataState/AccessState components (which stay
// green): this kit formalizes their ad-hoc loading/denied/error/empty branches
// into one closed 9-posture set so later waves never hand-roll state branching.

/**
 * The 9 first-class honest postures (blueprint §4):
 *  - loading           — skeleton, never a blank or bare spinner
 *  - empty             — the read succeeded but there is nothing yet (CTA allowed)
 *  - gated             — the org/workspace has not enabled this area
 *  - gate-closed       — an intentionally-closed lane (no write happened)
 *  - not-configured    — a provider/data source / missing backend isn't wired yet
 *  - unavailable       — a transient 503/outage (retryable)
 *  - permission-denied — the role lacks access (server-authoritative)
 *  - error             — an unexpected failure (retryable, shows a reference)
 *  - maturity-gated    — not enough signal yet (e.g. analytics until ingest)
 */
export type HonestPosture =
  | "loading"
  | "empty"
  | "gated"
  | "gate-closed"
  | "not-configured"
  | "unavailable"
  | "permission-denied"
  | "error"
  | "maturity-gated";

export const HONEST_POSTURES: readonly HonestPosture[] = [
  "loading",
  "empty",
  "gated",
  "gate-closed",
  "not-configured",
  "unavailable",
  "permission-denied",
  "error",
  "maturity-gated",
];

/**
 * Postures that are STABLE states, not failures — react-query must NOT retry
 * them and the UI shows no "Retry" affordance. `unavailable` and `error` are
 * the retryable postures (a transient outage / unexpected failure). `loading`
 * is the in-flight state and is likewise excluded here.
 */
export const NON_RETRYABLE_POSTURES: ReadonlySet<HonestPosture> = new Set<HonestPosture>([
  "empty",
  "gated",
  "gate-closed",
  "not-configured",
  "permission-denied",
  "maturity-gated",
]);

/** Postures that show a "Retry" affordance (the surface can recover by refetch). */
export const RETRYABLE_POSTURES: ReadonlySet<HonestPosture> = new Set<HonestPosture>([
  "unavailable",
  "error",
]);

export function isRetryablePosture(posture: HonestPosture): boolean {
  return RETRYABLE_POSTURES.has(posture);
}

/**
 * Neutral copy for each posture. Carried as data so <HonestState> can render a
 * default body while a caller can still override the title/message per surface.
 * Never reference a provider, an org name, or another tenant's data here.
 */
export const POSTURE_COPY: Record<HonestPosture, { title: string; body: string }> = {
  loading: { title: "Loading…", body: "Fetching the latest data." },
  empty: { title: "Nothing here yet", body: "No records match the current view." },
  gated: { title: "Not enabled", body: "This area is not enabled for your workspace yet." },
  "gate-closed": {
    title: "Action is closed",
    body: "This operation is intentionally closed right now. Nothing was changed.",
  },
  "not-configured": {
    title: "Not yet available",
    body: "This data source isn't configured yet. It will appear here once it's connected.",
  },
  unavailable: {
    title: "Data not loaded yet",
    body: "This data is temporarily unavailable. Try again in a moment.",
  },
  "permission-denied": {
    title: "You don't have access",
    body: "Your role doesn't have access to this. Ask an administrator if you need it.",
  },
  error: {
    title: "Something went wrong",
    body: "The request failed. You can retry, or share the support reference below.",
  },
  "maturity-gated": {
    title: "Not enough data yet",
    body: "There isn't enough signal to show this yet. Check back as the cohort progresses.",
  },
};
